import {useMemo} from 'react';

import {onlyItem} from '../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {useAppQuery} from '../../../../libs/shared/core/src/services/query/useAppQuery';
import {vacationResponderQueries} from '../queries';

/*
 * Types.
 */

export enum SettingsHomeIndicationTypesEnum {
  UNKNOWN = 'UNKNOWN',
  NONE = 'NONE',
  ONE = 'ONE',
  MANY = 'MANY',
}

interface SettingsHomeIndicationBase {
  type: SettingsHomeIndicationTypesEnum;
}

interface SettingsHomeIndicationUnknown extends SettingsHomeIndicationBase {
  type: SettingsHomeIndicationTypesEnum.UNKNOWN;
}

interface SettingsHomeIndicationNone extends SettingsHomeIndicationBase {
  type: SettingsHomeIndicationTypesEnum.NONE;
}

interface SettingsHomeIndicationOne<TId> extends SettingsHomeIndicationBase {
  type: SettingsHomeIndicationTypesEnum.ONE;
  onlyId: TId;
}

interface SettingsHomeIndicationMany extends SettingsHomeIndicationBase {
  type: SettingsHomeIndicationTypesEnum.MANY;
}

export type SettingsHomeIndication<TId> =
  | SettingsHomeIndicationUnknown
  | SettingsHomeIndicationNone
  | SettingsHomeIndicationOne<TId>
  | SettingsHomeIndicationMany;

export type VacationRespondersSettingsHomeIndication = SettingsHomeIndication<number>;

/*
 * Hook.
 */

export function useVacationRespondersSettingsHomeQuery(
  namespace: string,
): VacationRespondersSettingsHomeIndication {
  const {data: vacationResponders} = useAppQuery(vacationResponderQueries.twoByNamespace(namespace));

  return useMemo(() => {
    if (!vacationResponders) {
      return {
        type: SettingsHomeIndicationTypesEnum.UNKNOWN,
      };
    }

    const onlyVacationResponder = onlyItem(vacationResponders);
    if (onlyVacationResponder) {
      return {
        type: SettingsHomeIndicationTypesEnum.ONE,
        onlyId: onlyVacationResponder.id,
      };
    }

    if (vacationResponders.length === 0) {
      return {
        type: SettingsHomeIndicationTypesEnum.NONE,
      };
    }

    return {
      type: SettingsHomeIndicationTypesEnum.MANY,
    };
  }, [vacationResponders]);
}
